/**
 * A button that shows only an icon, with the accessible name and tap
 * target an icon-only control needs: `label` becomes the aria-label and
 * tooltip, and the button is 44px square, matching the app's base touch
 * target rule in index.css (WCAG 2.5.5). Native button + Tailwind, per the
 * Flowbite Button rendering issues.
 * See docs/frontend/components/icon-button.md.
 */
import type { ComponentType, SVGProps } from 'react';

interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** What the button does, e.g. "Edit phone number"; read by screen readers and shown on hover */
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** "danger" colors it for removals */
  tone?: 'default' | 'danger';
}

const TONES = {
  default: 'text-gray-500 hover:text-blue-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-blue-300 dark:hover:bg-gray-700',
  danger: 'text-gray-500 hover:text-red-700 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-300 dark:hover:bg-red-900/30',
};

export const IconButton: React.FC<IconButtonProps> = ({
  label,
  icon: Icon,
  tone = 'default',
  type = 'button',
  className = '',
  ...props
}) => (
  <button
    type={type}
    aria-label={label}
    title={label}
    className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 ${TONES[tone]} ${className}`}
    {...props}
  >
    <Icon className="h-4 w-4" aria-hidden="true" />
  </button>
);
